/**
 * Tilni almashtirish -- yagona yo'l (02-yechim, 8-band: server tasdig'i, rad etilsa orqaga qaytish):
 * - UI darhol yangi tilga o'tadi (lug'atlar brauzerda, serverni kutmaydi);
 * - profilga (User.language) saqlash ketma-ket ketadi: server tartibi = bosish tartibi;
 * - server rad etsa yoki tarmoq bo'lmasa -- oxirgi tasdiqlangan tilga qaytadi va xabar chiqadi;
 * - saqlash davom etayotganda profilning eski nusxasi UI tilini orqaga qaytarmaydi.
 * Login sahifasida qo'lda tanlangan til kirgandan keyin profilga yoziladi.
 */
import { useEffect } from "react";
import i18n, { currentLanguage, isLanguage, setLanguage, type Language } from "@/i18n";
import { api } from "@/lib/api";
import { queryClient } from "@/lib/queryClient";
import { toast } from "@/components/ui/toast";
import { meKey } from "./hooks";
import type { Me } from "./types";

/** Kirishdan oldin (login, apply) qo'lda tanlangan til; shu tab uchun, bir martalik */
const CHOSEN_KEY = "dh.lang.chosen";

let saving = 0; // jarayondagi saqlashlar soni
let seq = 0; // eng oxirgi tanlov raqami
let chain: Promise<unknown> = Promise.resolve(); // PATCH'lar navbati

export function isSavingLanguage(): boolean {
  return saving > 0;
}

export async function changeLanguage(lang: Language, { persist }: { persist: boolean }): Promise<void> {
  const before = currentLanguage();
  setLanguage(lang);
  if (!persist) {
    rememberChosen(lang);
    return;
  }
  forgetChosen();
  const mine = ++seq;
  saving += 1;
  const run = chain.then(() => api.patch<Me>("/api/auth/me/", { language: lang }));
  chain = run.catch(() => undefined);
  try {
    queryClient.setQueryData(meKey, await run); // server tasdiqladi
  } catch {
    // Faqat eng oxirgi tanlov orqaga qaytaradi: undan keyingi bosish o'zi hal qiladi
    if (mine === seq) {
      const confirmed = queryClient.getQueryData<Me | null>(meKey)?.language;
      setLanguage(isLanguage(confirmed) ? confirmed : before);
      toast.error(i18n.t("errors.languageNotSaved"));
    }
  } finally {
    saving -= 1;
  }
}

export type SyncAction = { kind: "none" } | { kind: "apply"; lang: Language } | { kind: "persist"; lang: Language };

/** Profil tili (server) kelganda nima qilinadi. Sof funksiya: testda tekshiriladi. */
export function profileSyncAction(server: Language, current: Language, chosen: Language | null, savingNow: boolean): SyncAction {
  if (chosen && chosen !== server) return { kind: "persist", lang: chosen }; // login sahifasidagi tanlov ustun
  if (savingNow || server === current) return { kind: "none" }; // saqlash javobi o'zi hal qiladi
  return { kind: "apply", lang: server };
}

/**
 * Profil tilini UI ga qo'llaydi: kirish, boshqa oyna yoki qurilmadagi o'zgarish (real-time).
 * Faqat server qiymati o'zgarganda ishlaydi -- foydalanuvchi bosgan tilni qaytarib qo'ymaydi.
 */
export function useProfileLanguage(server: string | undefined | null) {
  useEffect(() => {
    if (!isLanguage(server)) return;
    const action = profileSyncAction(server, currentLanguage(), takeChosenLanguage(), isSavingLanguage());
    if (action.kind === "persist") void changeLanguage(action.lang, { persist: true });
    else if (action.kind === "apply") setLanguage(action.lang);
  }, [server]);
}

export function takeChosenLanguage(): Language | null {
  try {
    const value = sessionStorage.getItem(CHOSEN_KEY);
    sessionStorage.removeItem(CHOSEN_KEY);
    return isLanguage(value) ? value : null;
  } catch {
    return null;
  }
}

function rememberChosen(lang: Language) {
  try {
    sessionStorage.setItem(CHOSEN_KEY, lang);
  } catch {
    /* ixtiyoriy */
  }
}

function forgetChosen() {
  try {
    sessionStorage.removeItem(CHOSEN_KEY);
  } catch {
    /* ixtiyoriy */
  }
}
